/**
 * Shared display constants for the Strategy Adjustment Engine page.
 * Mirrors backend `AdjustableStrategyType` / `leg_synthesis.
 * _RECOMMENDATION_KEY_TO_ADJUSTABLE_TYPE` — kept here rather than derived
 * at runtime since these are small, fixed lookup tables on both sides.
 */
import type { AdjustableStrategyType, BrokerCheckStatus, BrokerName, DataQualitySource } from "@/types";

export const STRATEGY_TYPE_LABELS: Record<AdjustableStrategyType, string> = {
  CALENDAR_SPREAD: "Calendar Spread",
  DIAGONAL_SPREAD: "Diagonal Spread",
  IRON_CONDOR: "Iron Condor",
  IRON_FLY: "Iron Fly (Butterfly)",
  BROKEN_WING_IRON_CONDOR: "Broken Wing Iron Condor",
  SHORT_STRANGLE: "Short Strangle",
  LONG_STRADDLE: "Long Straddle",
  LONG_STRANGLE: "Long Strangle",
  BULL_CALL_SPREAD: "Bull Call Spread",
  BEAR_CALL_SPREAD: "Bear Call Spread",
  BULL_PUT_SPREAD: "Bull Put Spread",
  BEAR_PUT_SPREAD: "Bear Put Spread",
  LONG_CALL: "Long Call",
  LONG_PUT: "Long Put",
};

export const ADJUSTABLE_STRATEGY_TYPES = Object.keys(STRATEGY_TYPE_LABELS) as AdjustableStrategyType[];

/** The recommendation engine's scored strategy `key`s map 1:1 onto
 * `AdjustableStrategyType` by name, with one naming difference
 * ("IRON_BUTTERFLY" there vs. this engine's "IRON_FLY") — see backend
 * `leg_synthesis.py`'s own mapping table, which this must stay in sync
 * with. Returns `null` for recommendation keys this engine has no leg
 * template for (Covered Call / Protective Put — never actually the
 * engine's `best_match` in practice). */
export function mapRecommendationKeyToAdjustableType(strategyKey: string): AdjustableStrategyType | null {
  if (strategyKey === "IRON_BUTTERFLY") return "IRON_FLY";
  return (ADJUSTABLE_STRATEGY_TYPES as string[]).includes(strategyKey)
    ? (strategyKey as AdjustableStrategyType)
    : null;
}

export function fmtCurrency(value?: number | null, decimals = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}₹${Math.abs(value).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function fmtNum(value?: number | null, decimals = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Phase 2 data-quality labeling (LIVE/OBSERVED, MODEL ESTIMATE,
 * THEORETICAL, UNAVAILABLE) — one label + color per
 * `DataQualitySource`, used everywhere a value's provenance is shown. */
export const DATA_QUALITY_LABELS: Record<DataQualitySource, string> = {
  OBSERVED: "Observed",
  USER_OVERRIDE: "Your Override",
  MODEL_ESTIMATE: "Model Estimate",
  THEORETICAL: "Theoretical",
  UNAVAILABLE: "Unavailable",
};

export const DATA_QUALITY_COLORS: Record<DataQualitySource, "success" | "info" | "warning" | "default"> = {
  OBSERVED: "success",
  USER_OVERRIDE: "info",
  MODEL_ESTIMATE: "warning",
  THEORETICAL: "warning",
  UNAVAILABLE: "default",
};

/** Phase 3 (Broker Validation Layer) — display label per broker (the chip
 * selector's own text) and the ✓/⚠/✕ status→color/label mapping shared by
 * every check and every leg result. */
export const BROKER_DISPLAY_LABELS: Record<BrokerName, string> = {
  DHAN: "Dhan",
  ZERODHA: "Zerodha",
  ICICI_BREEZE: "ICICI Direct",
  GROWW: "Groww",
  ANGEL_ONE: "Angel One",
};

export const BROKER_CHECK_STATUS_COLORS: Record<BrokerCheckStatus, "success" | "warning" | "error"> = {
  VALID: "success",
  WARNING: "warning",
  INVALID: "error",
};

export const BROKER_CHECK_STATUS_SYMBOLS: Record<BrokerCheckStatus, string> = {
  VALID: "✓",
  WARNING: "⚠",
  INVALID: "✕",
};
